<template>
  <!-- ローディング画面 -->
  <LoadingScreen v-if="isLoading" />
  <div v-else>
    <PageNavigation />
    <div class="margin-section">
      <div>
        <InsertImage titlePage="TREATMENT" titlePageJP="診療内容"/>
      </div>
      <div v-if="treatmentData" class="half-block-content">
        <!-- パンくずリストを追加 -->
        <nav aria-label="breadcrumb" class="breadcrumb">
          <ul>
            <li>
              <router-link to="/">ホーム</router-link>
            </li>
            <li>
              <router-link :to="{ path: '/', hash: '#treatment-category-list' }">診療内容</router-link>
            </li>
            <li @click="navigateToTreatmentList(treatmentData.treatment_category)">
              <a>{{ treatmentData.treatment_category_name }}</a>
            </li>
            <li aria-current="page"> <!-- すでにそのページにいる -->
              <span>{{ treatmentData.treatment_name }}</span>
            </li>
          </ul>
        </nav>
        <h2 v-fade-in>{{ treatmentData.treatment_name }}</h2>
        <div class="center">
          <img alt="Circle objects" src="@/assets/images/circle_objects.png" class="display-circle-obj" width="180" height="21.77" />
        </div>
        <div class="treatment-container content-padding">
          <!-- Presigned URL を表示 -->
          <div class="image-container" v-if="treatmentData.image_path">
            <img :src="treatmentData.image_path" alt="Treatment Image" class="treatment-image" />
          </div>
          <div class="description" v-fade-in>
            <h3>概要</h3>
            <p v-html="formatContent(treatmentData.symptoms_description)"></p>
          </div>
          <div class="main-content" v-fade-in>
            <h3>{{ treatmentData.title }}</h3>
            <p v-html="formatContent(treatmentData.content)"></p>
          </div>
          <div class="sub-content" v-if="treatmentData.details.length > 0">
            <div v-for="(detail, index) in treatmentData.details" :key="index" v-fade-in>
              <h3>{{ detail.sub_title }}</h3>
              <p v-html="formatContent(detail.sub_content)"></p>
            </div>
          </div>
          <div class="table-content" v-if="treatmentData.table_description" v-fade-in>
            <p v-html="formatContent(treatmentData.table_description)" class="table-description"></p>
            <div class="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th v-for="header in treatmentData.headers" :key="header">{{ header }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in treatmentData.rows" :key="row">
                    <td v-for="cell in row" :key="cell">{{ cell }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="half-block-content">診療内容情報はありません。</div>
    </div>
    <PageFooter />
  </div>
</template>

<script>
import { formatContent } from '@/utils/utils';
import treatmentService from '@/services/treatmentService';
import PageNavigation from '@/components/PageNavigation.vue';
import PageFooter from '@/components/PageFooter.vue';
import LoadingScreen from '@/components/LoadingScreen.vue';
import InsertImage from '@/components/InsertImage.vue';

export default {
  name: 'TreatmentDetailPage',
  components: {
    PageNavigation,
    PageFooter,
    LoadingScreen,
    InsertImage,
  },
  data() {
    return {
      treatmentData: {},
      isLoading: true // ローディング状態
    };
  },
  computed: {
    treatmentCategoryName() {
      // URLパラメータから治療カテゴリ名を取得
      const category = this.$route.params.treatmentCategory;
      switch (category) {
        case 'general_internal_medicine':
          return '一般内科';
        case 'dermatology':
          return '皮膚科';
        case 'urology':
          return '泌尿器科';
        case 'exam_immunization':
          return '検査・予防接種';
        default:
          return '';
      }
    }
  },
  created() {
    // 遷移時にAPIを呼び出してデータを取得
    const treatmentCategory = this.$route.params.treatmentCategory;
    const treatmentId = this.$route.params.treatmentId;
    this.fetchTreatment(treatmentCategory, treatmentId);
  },
  methods: {
    formatContent,
    // APIを呼び出してデータを取得
    async fetchTreatment(treatmentCategory, treatmentId) {
      try {
        this.treatmentData = await treatmentService.getTreatment(treatmentCategory, treatmentId); // BEからデータを取得
        // console.log('image_path:', this.treatmentData.image_path)
      } catch (error) {
        console.error('APIエラー:', error);
      } finally {
        // ローディング終了
        this.isLoading = false;
        this.adjustTableColumnWidths();
      }
    },
    navigateToTreatmentList(treatmentCategory) {
      // 診療内容のページに遷移
      this.$router.push(`/treatment/${treatmentCategory}`);
    },
    adjustTableColumnWidths() {
      this.$nextTick(() => {
        if (!this.treatmentData || !this.treatmentData.headers || this.treatmentData.headers.length === 0) {
          return;
        }

        // スマホ幅では画面に収まるようCSS側で折り返し・縮小させるため、pxでの幅固定は行わない
        if (window.innerWidth <= 768) {
          return;
        }

        const rows = document.querySelectorAll('tr');
        if (rows.length === 0) return;

        // 最初の行のセル数から列数を取得
        const colCount = rows[0].querySelectorAll('td, th').length;
        const maxWidths = new Array(colCount).fill(0);

        // 列ごとに最大幅を計算
        for (let colIndex = 0; colIndex < colCount; colIndex++) {
          rows.forEach(row => {
            const cell = row.querySelectorAll('td, th')[colIndex];
            if (cell) {
              // 一旦autoにして自然なサイズを取得
              cell.style.width = 'auto';
              const cellWidth = cell.offsetWidth;
              maxWidths[colIndex] = Math.max(maxWidths[colIndex], cellWidth);
            }
          });
        }

        // 計算した最大幅を各セルに適用
        rows.forEach(row => {
          const cells = row.querySelectorAll('td, th');
          cells.forEach((cell, i) => {
            if (cell) {
              cell.style.width = maxWidths[i] + 'px';
            }
          });
        });
      });
    }
  }
}
</script>

<style scoped>
/* ulとolのリストのスタイルをリセット */
.treatment-list ul, ol {
  padding: 0;
  margin: 0;
}

.treatment-container {
  display: block;
  text-align: left;
  height: auto;
  width: 90%;
  margin: 0px 3vw;
  gap: 30px;
}

.treatment-container p {
  line-height: 2;
  margin-top: 0.2em;
  margin-bottom: 2em;
  padding: 0 0.3em;
}

.description h3, .main-content h3 {
  position: relative;
  padding: 0.3rem 0.2em;
  margin: 0;
  color: #333;
  background: linear-gradient(90deg, #15d8ce 0%, #5c16dd 100%);
  background-repeat: no-repeat;
  background-size: 100% 5px;
  background-position: bottom;
  font-weight: bold;
  font-size: clamp(18px, 3.6vw, 26px);
  width: fit-content;
}

.sub-content h3 {
  font-size: clamp(16px, 3.4vw, 22px);
  padding: 0.3rem 0.2em;
  color: #1757e0;
  margin-bottom: 0;
  font-weight: bold;
}

.main-content {
  margin-top: 2vw;
  margin-bottom: 2vw;
}


.image-container {
  text-align: center;
}

.image-container img {
  float: right;
}

.treatment-image {
  width: 35%;
  height: auto;
  border-radius: 8px;
  margin: 10px;
  margin-top: 0;
}

.table-content {
  text-align: center;
}

.table-scroll {
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

table{
  border-collapse: collapse;
  text-indent: initial;
  table-layout: auto;
  margin-left: auto;
  margin-right: auto;
}

table th,table td{
  color: #505050;
  border:solid 1px #ddd;
  text-align: center;
  padding: .5em .75em;
  vertical-align: middle;
  display: table-cell;
  unicode-bidi: isolate;
}

/* 一番左の列のセルにスタイルを適用 */
table td:first-child {
  background: #f7f7f7;
  border: 1px solid #ddd;
  color: #505050;
  text-align: center;
  white-space: nowrap; /* テキストを折り返さない */
  font-weight: bolder;
  width: 0;
}

table th {
  color: #fff;
  background: #032264;
  font-weight: bold;
}

.table-content .table-description {
  margin-bottom: 0.2em;
  font-weight: 700;
}

/* PC用の3列表示 */
@media (min-width: 768px) {

}

@media (max-width: 768px) {

  .treatment-container {
    justify-content: center;
    width: auto;
  }

  .treatment-image {
    width: 80%;
    height: auto;
    border-radius: 8px;
    margin: 10px;
    margin-top: 0;
  }

  .image-container img {
    float: none;
  }

  .table-scroll {
    overflow-x: visible;
  }

  table {
    table-layout: fixed;
  }

  table th, table td {
    padding: 0.4em 0.3em;
    font-size: clamp(11px, 3vw, 14px);
    white-space: normal;
    word-break: break-word;
    overflow-wrap: break-word;
  }

  table td:first-child {
    white-space: normal;
    width: auto;
  }

}

</style>